import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Truck, RefreshCw, Phone, PhoneCall, MapPin, ArrowLeft, Banknote, CreditCard, Tag, Radar, FileDown, Mail, Lock, XCircle, LogOut, CheckCircle2, Undo2, MessageSquareText, AlertTriangle, Printer, IndianRupee, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API, fmt } from "../constants/site";
import Logo from "../components/Logo";

const TOKEN_KEY = "omni_admin_token";
const getToken = () => sessionStorage.getItem(TOKEN_KEY) || "";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const STATUS_LABEL = {
  confirmed: "Confirmed",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
  returned: "Returned",
  return_requested: "Return requested",
};
const STATUS_CHIP = {
  confirmed: "bg-oat-200 text-charcoal-700",
  shipped: "bg-sage-500 text-white",
  delivered: "bg-sage-700 text-white",
  cancelled: "bg-[#fdecec] text-[#b91c1c]",
  returned: "bg-[#efe7d8] text-[#8a6a2f]",
  return_requested: "bg-amber-100 text-amber-700",
};

export default function AdminOrders() {
  const [token, setToken] = useState(getToken());
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [srConfigured, setSrConfigured] = useState(false);
  const [trackingText, setTrackingText] = useState({});
  const [awbInputs, setAwbInputs] = useState({});
  const [awbCourier, setAwbCourier] = useState({});
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("active");

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setOrders([]);
    setPassword("");
  };

  const adminFetch = async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}`, ...(options.headers || {}) },
    });
    if (res.status === 401) {
      logout();
      throw new Error("Session ended — please log in again");
    }
    return res;
  };

  // Proxy/upgrade pages (HTML) must never crash the dashboard with "<!DOCTYPE" errors.
  const parseJson = async (res, fallbackMsg) => {
    try {
      return await res.json();
    } catch {
      throw new Error(fallbackMsg || "Server is busy or updating — please try again in a few seconds");
    }
  };

  // Live dashboard: silently re-fetch orders every 15 s so Monika's call results appear on their own.
  const loadRef = useRef(() => {});
  useEffect(() => {
    if (!token) return;
    const iv = setInterval(() => loadRef.current(true), 15000);
    return () => clearInterval(iv);
  }, [token]);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await adminFetch("/orders");
      const data = await res.json();
      // API returns newest first — keep it that way so new orders are always on top.
      setOrders(Array.isArray(data) ? data : []);
      try {
        const sRes = await adminFetch("/admin/stats");
        const sData = await sRes.json();
        if (sRes.ok) setStats(sData);
      } catch {
        // stats panel is auxiliary — never block the orders list
      }
    } catch (e) {
      if (e.message !== "Failed to fetch") toast.error(e.message || "Could not load orders");
    } finally {
      setLoading(false);
    }
  };
  loadRef.current = load;

  const removeAllCancelled = async () => {
    const n = orders.filter((o) => o.status === "cancelled").length;
    if (!n) {
      toast.info("No cancelled orders to remove");
      return;
    }
    if (!window.confirm(`Remove ALL ${n} cancelled order${n === 1 ? "" : "s"} permanently? This cannot be undone.`)) return;
    try {
      const res = await adminFetch("/admin/cancelled-orders", { method: "DELETE" });
      const data = await parseJson(res, "Could not remove cancelled orders — please try again");
      if (!res.ok) throw new Error(data.detail || "Could not remove cancelled orders");
      toast.success(`Removed ${data.deleted} cancelled order${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.message || "Could not remove cancelled orders");
    }
  };

  const removeFailedPayments = async () => {
    const n = orders.filter((o) => o.payment_method === "razorpay" && o.payment_status !== "paid").length;
    if (!n) {
      toast.info("No unpaid Pay Online orders to remove");
      return;
    }
    if (!window.confirm(`Remove ALL ${n} order${n === 1 ? "" : "s"} with incomplete Pay Online payment? This cannot be undone.`)) return;
    try {
      const res = await adminFetch("/admin/failed-payments", { method: "DELETE" });
      const data = await parseJson(res, "Could not remove unpaid orders — please try again");
      if (!res.ok) throw new Error(data.detail || "Could not remove unpaid orders");
      toast.success(`Removed ${data.deleted} unpaid order${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(e.message || "Could not remove unpaid orders");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Incorrect password");
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setLoginError(err.message || "Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    load();
    fetch(`${API}/shiprocket/status`)
      .then((r) => r.json())
      .then((d) => setSrConfigured(!!d.configured))
      .catch(() => {});
  }, [token]);

  const setStatus = async (num, i, status) => {
    try {
      const res = await adminFetch(`/orders/${num}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not update order");
      setOrders((o) => o.map((x, j) => (j === i ? { ...x, status } : x)));
      const msgs = {
        shipped: `${num} marked as shipped`,
        delivered: `${num} marked as delivered 🎉`,
        cancelled: `${num} cancelled — customer notified by email`,
        returned: `${num} marked as returned`,
      };
      toast.success(msgs[status] || `${num} updated`);
    } catch (e) {
      toast.error(e.message || "Could not update order");
    }
  };

  const cancelOrder = (num, i) => {
    if (!window.confirm(`Cancel order ${num}? The customer will receive a cancellation email.`)) return;
    setStatus(num, i, "cancelled");
  };

  const returnOrder = (num, i) => {
    if (!window.confirm(`Mark order ${num} as returned?`)) return;
    setStatus(num, i, "returned");
  };

  const undoReturn = async (num) => {
    if (!window.confirm(`Move order ${num} back to Confirmed? Nothing will be sent to the customer.`)) return;
    try {
      const res = await adminFetch(`/orders/${num}/undo-return`, { method: "POST" });
      const data = await parseJson(res, "Could not restore the order — please try again");
      if (!res.ok) throw new Error(data.detail || "Could not restore the order");
      toast.success(`${num} moved back to Confirmed — no message sent to the customer`);
      load();
    } catch (e) {
      toast.error(e.message || "Could not restore the order");
    }
  };

  const [delhivery, setDelhivery] = useState({});
  const [callState, setCallState] = useState({});
  const [trackInfo, setTrackInfo] = useState({});

  const checkEstimate = async (num, i) => {
    setDelhivery((s) => ({ ...s, [i]: { ...s[i], loading: true } }));
    try {
      const res = await adminFetch(`/orders/${num}/delhivery/estimate`);
      const data = await parseJson(res, "Could not reach Delhivery — please try again in a few seconds");
      if (!res.ok) throw new Error(data.detail || "Delhivery estimate failed");
      setDelhivery((s) => ({ ...s, [i]: { loading: false, estimate: data } }));
    } catch (e) {
      setDelhivery((s) => ({ ...s, [i]: { loading: false, error: e.message } }));
    }
  };

  const bookDelhivery = async (num, i) => {
    const est = delhivery[i]?.estimate;
    const charge = est && est.total_amount != null && est.total_amount > 0 ? ` (charge as per Delhivery: ₹${est.total_amount})` : " (billed as per your Delhivery plan)";
    if (!window.confirm(`Approve and book this shipment with Delhivery${charge}? A real AWB will be created.`)) return;
    setDelhivery((s) => ({ ...s, [i]: { ...s[i], loading: true } }));
    try {
      const res = await adminFetch(`/orders/${num}/delhivery/book`, { method: "POST" });
      const data = await parseJson(res, "Booking didn't go through — the server is busy or updating. Please check this order and try again in a few seconds.");
      if (!res.ok) throw new Error(data.detail || "Delhivery booking failed");
      // Delhivery takes 30-60s, so the booking runs in the background — poll until the real AWB appears.
      for (let t = 0; t < 30; t++) {
        await new Promise((r) => setTimeout(r, 3000));
        const sRes = await adminFetch(`/orders/${num}/delhivery/book-status`);
        const sData = await parseJson(sRes, "Checking booking status failed — please refresh the dashboard in a minute");
        if (!sRes.ok) throw new Error(sData.detail || "Booking status failed");
        if (sData.booking_state === "booked" && sData.shipment?.awb) {
          setOrders((o) => o.map((x, j) => (j === i ? { ...x, shipment: sData.shipment, status: sData.status || "shipped" } : x)));
          toast.success(`Booked with Delhivery — AWB ${sData.shipment.awb}`);
          return;
        }
        if (sData.booking_state === "failed") throw new Error(sData.delhivery_booking_error || "Delhivery booking failed");
      }
      toast.info("Delhivery is still confirming the booking in the background — refresh this page in a minute to see the AWB");
    } catch (e) {
      toast.error(e.message || "Delhivery booking failed");
    } finally {
      setDelhivery((s) => ({ ...s, [i]: { ...s[i], loading: false } }));
    }
  };

  const callMonika = async (num, i) => {
    setCallState((s) => ({ ...s, [i]: { loading: true } }));
    try {
      const res = await adminFetch(`/orders/${num}/bolna-call`, { method: "POST" });
      const data = await parseJson(res, "Could not start the call — please try again in a few seconds");
      if (!res.ok) throw new Error(data.detail || "Could not start the call");
      if (data.call_id) toast.success("Monika is calling the customer now");
      else toast.info(data.skipped || "Call not placed");
      load();
    } catch (e) {
      toast.error(e.message || "Could not start the call");
    } finally {
      setCallState((s) => ({ ...s, [i]: { loading: false } }));
    }
  };

  const [returnPick, setReturnPick] = useState({});
  const [refundUpi, setRefundUpi] = useState({});

  const bookReturnPickup = async (num, i) => {
    setReturnPick((s) => ({ ...s, [i]: { loading: true } }));
    try {
      const res = await adminFetch(`/orders/${num}/delhivery/return-pickup`, { method: "POST" });
      const data = await parseJson(res, "Return pickup booking failed — please try again in a few seconds");
      if (!res.ok) throw new Error(data.detail || "Return pickup booking failed");
      for (let t = 0; t < 30; t++) {
        await new Promise((r) => setTimeout(r, 3000));
        const sRes = await adminFetch(`/orders/${num}/delhivery/return-pickup-status`);
        const sData = await parseJson(sRes, "Checking pickup status failed — please refresh in a minute");
        if (!sRes.ok) throw new Error(sData.detail || "Pickup status failed");
        if (sData.state === "booked" && sData.return_pickup?.awb) {
          setOrders((o) => o.map((x, j) => (j === i ? { ...x, return_pickup: sData.return_pickup } : x)));
          toast.success(`Return pickup booked — AWB ${sData.return_pickup.awb}`);
          return;
        }
        if (sData.state === "failed") throw new Error(sData.error || "Return pickup booking failed");
      }
      toast.info("Delhivery is still confirming the pickup in the background — refresh in a minute");
    } catch (e) {
      toast.error(e.message || "Return pickup booking failed");
    } finally {
      setReturnPick((s) => ({ ...s, [i]: { loading: false } }));
    }
  };

  const recordRefund = async (num, i, upi) => {
    try {
      const res = await adminFetch(`/orders/${num}/refund`, {
        method: "POST",
        body: JSON.stringify({ upi }),
      });
      const data = await parseJson(res, "Could not record the refund — please try again");
      if (!res.ok) throw new Error(data.detail || "Could not record the refund");
      setOrders((o) => o.map((x, j) => (j === i ? { ...x, refund_upi: data.refund_upi, refund_amount: data.refund_amount, refunded_at: data.refunded_at } : x)));
      toast.success(`Refund of ₹${data.refund_amount} recorded — send it via UPI to ${data.refund_upi}`);
    } catch (e) {
      toast.error(e.message || "Could not record the refund");
    }
  };

  const labelPdf = async (num, i, doPrint) => {
    try {
      const res = await adminFetch(`/orders/${num}/delhivery/label`);
      if (!res.ok) {
        const d = await parseJson(res, "Label fetch failed — please try again in a few seconds");
        throw new Error(d.detail || "Label fetch failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (doPrint) {
        const w = window.open(url);
        if (w) w.addEventListener("load", () => w.print());
        toast.info("Label opened — use your browser print dialog");
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `OmniMist-${num}-label.pdf`;
        a.click();
        toast.success("Official Delhivery label downloaded");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.message || "Label fetch failed");
    }
  };

  const refreshTrack = async (num, i) => {
    setTrackInfo((s) => ({ ...s, [i]: { ...s[i], loading: true } }));
    try {
      const res = await adminFetch(`/orders/${num}/delhivery/track`);
      const data = await parseJson(res, "Tracking didn't load — please try again in a few seconds");
      if (!res.ok) throw new Error(data.detail || "Tracking failed");
      setTrackInfo((s) => ({ ...s, [i]: { loading: false, ...data } }));
      toast.success(`Delhivery: ${data.status}${data.location ? ` · ${data.location}` : ""}`);
    } catch (e) {
      setTrackInfo((s) => ({ ...s, [i]: { loading: false } }));
      toast.error(e.message || "Tracking failed");
    }
  };

  const saveAwb = async (num, i) => {
    const awb = (awbInputs[i] || "").trim();
    if (!awb) {
      toast.error("Paste the AWB / tracking ID first");
      return;
    }
    const courier = awbCourier[i] || "Bluedart";
    try {
      const res = await adminFetch(`/orders/${num}/awb`, {
        method: "POST",
        body: JSON.stringify({ awb, courier }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Could not save AWB");
      setOrders((o) =>
        o.map((x, j) => (j === i ? { ...x, shipment: data.shipment, status: "shipped" } : x))
      );
      toast.success(`${num} shipped via ${data.shipment.courier} — AWB ${awb} saved. Tap SMS or WhatsApp to send tracking.`);
    } catch (e) {
      toast.error(e.message || "Could not save AWB");
    }
  };

  const shipViaRocket = async (num, i) => {
    try {
      const res = await adminFetch(`/orders/${num}/ship`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Shiprocket error");
      setOrders((o) =>
        o.map((x, j) =>
          j === i ? { ...x, shipment: data.shipment, status: "shipped" } : x
        )
      );
      toast.success(`${num} shipped via Shiprocket — AWB ${data.shipment.awb}`);
    } catch (e) {
      toast.error(e.message || "Could not create shipment");
    }
  };

  const track = async (num, i) => {
    try {
      const res = await adminFetch(`/orders/${num}/tracking`);
      const data = await res.json();
      if (!res.ok || !data.tracking) throw new Error(data.detail || "No tracking yet");
      const t = data.tracking;
      setTrackingText((s) => ({ ...s, [i]: `${t.status || "In transit"} · ${t.courier || ""}` }));
      toast.success(`Tracking: ${t.status || "In transit"}`);
    } catch (e) {
      toast.error(e.message || "Could not fetch tracking");
    }
  };

  const bluedartUrl = (awb) =>
    `https://www.bluedart.com/trackdart.aspx?trackNo=${encodeURIComponent(awb || "")}`;

  const trackUrl = (o) =>
    o.shipment?.courier === "Delhivery"
      ? `https://www.delhivery.com/track?awb=${encodeURIComponent(o.shipment?.awb || "")}`
      : bluedartUrl(o.shipment?.awb);

  const waLink = (o) => {
    const digits = (o.customer?.phone || "").replace(/\D/g, "").slice(-10);
    if (!digits) return null;
    const first = (o.customer?.full_name || "").split(" ")[0];
    let msg;
    if (o.status === "shipped") {
      msg =
        `Hi ${first}! Your OmniMist order ${o.order_number} has shipped via ${o.shipment?.courier || "Bluedart"}.` +
        (o.shipment?.awb ? ` AWB: ${o.shipment.awb}.` : "") +
        ` Track live: ${bluedartUrl(o.shipment?.awb)} Track on our site: ${window.location.origin}/track?order=${o.order_number} Thank you for shopping with OmniMist Kitchen Craft!`;
    } else {
      msg =
        `Hi ${first}! Thank you for your order ${o.order_number} at OmniMist Kitchen Craft — ` +
        `${fmt(o.total)} for the OmniMist™ 2-in-1 Oil Sprayer & Dispenser. ` +
        `Please confirm your order here: ${window.location.origin}/confirm?order=${o.order_number} ` +
        `(you can also cancel there if needed). Thank you!`;
    }
    return `https://wa.me/91${digits}?text=${encodeURIComponent(msg)}`;
  };

  const smsLink = (o) => {
    const digits = (o.customer?.phone || "").replace(/\D/g, "").slice(-10);
    if (!digits) return null;
    const first = (o.customer?.full_name || "").split(" ")[0];
    const msg =
      `Hi ${first}! Your OmniMist order ${o.order_number} has shipped via ${o.shipment?.courier || "Bluedart"}.` +
      (o.shipment?.awb ? ` AWB: ${o.shipment.awb}.` : "") +
      ` Track live: ${bluedartUrl(o.shipment?.awb)} or ${window.location.origin}/track?order=${o.order_number} — Thank you!`;
    return `sms:+91${digits}?&body=${encodeURIComponent(msg)}`;
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-oat-100 px-5" data-testid="admin-gate">
        <form
          onSubmit={login}
          className="w-full max-w-sm rounded-3xl border border-charcoal-900/10 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(43,43,43,0.35)]"
          data-testid="admin-login-form"
        >
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sage-100">
            <Lock className="h-5 w-5 text-sage-700" />
          </div>
          <h1 className="mt-4 text-center font-serif text-2xl text-charcoal-900">Owner Access</h1>
          <p className="mt-1 text-center text-sm text-charcoal-500">
            Enter your dashboard password to view customer orders.
          </p>
          <input
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Dashboard password"
            data-testid="admin-password-input"
            className="mt-6 w-full rounded-full border border-charcoal-900/15 bg-oat-50 px-5 py-3 text-base text-charcoal-900 outline-none transition-colors focus:border-sage-500"
          />
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-btn"
            className="mt-3 w-full rounded-full bg-charcoal-900 px-5 py-3 text-xs font-medium uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600 disabled:opacity-60"
          >
            {loggingIn ? "Checking…" : "Unlock Dashboard"}
          </button>
          {loginError && (
            <p className="mt-3 text-center text-xs font-medium text-[#b91c1c]" data-testid="admin-login-error">
              {loginError}
            </p>
          )}
          <Link to="/" className="mt-4 block text-center text-xs text-charcoal-500 transition-colors hover:text-sage-600">
            ← Back to store
          </Link>
        </form>
      </div>
    );
  }

  const active = orders.filter((o) => !["cancelled", "returned", "delivered"].includes(o.status));
  const delivered = orders.filter((o) => o.status === "delivered");
  const closed = orders.filter((o) => ["cancelled", "returned"].includes(o.status));
  const list = tab === "active" ? active : tab === "delivered" ? delivered : closed;

  const soldOrders = orders.filter((o) => !["cancelled", "returned"].includes(o.status));
  const revenue = soldOrders.reduce((s, o) => s + o.total, 0);
  const codCount = soldOrders.filter((o) => o.payment_method === "cod").length;

  const tabs = [
    { id: "active", label: "Active", count: active.length, testid: "admin-tab-active" },
    { id: "delivered", label: "Delivered", count: delivered.length, testid: "admin-tab-delivered" },
    { id: "closed", label: "Cancelled & Returned", count: closed.length, testid: "admin-tab-closed" },
  ];
  const unpaidCount = orders.filter((o) => o.payment_method === "razorpay" && o.payment_status !== "paid").length;

  return (
    <div className="min-h-screen bg-oat-100" data-testid="admin-orders-page">
      <header className="border-b border-charcoal-900/10 bg-oat-50/90 backdrop-blur">
        <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between gap-2 px-5">
          <Logo />
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              aria-label="Refresh orders"
              className="flex items-center gap-2 rounded-full border border-charcoal-900/15 bg-white px-3 py-2 text-xs font-medium uppercase tracking-[0.14em] text-charcoal-900 transition-colors hover:border-sage-500 hover:text-sage-600 sm:px-4"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              aria-label="Logout"
              className="flex items-center gap-2 rounded-full border border-charcoal-900/15 bg-white px-3 py-2 text-xs font-medium uppercase tracking-[0.14em] text-charcoal-900 transition-colors hover:border-sage-500 hover:text-sage-600 sm:px-4"
            >
              <LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Logout</span>
            </button>
            <Link
              to="/"
              data-testid="admin-back-link"
              aria-label="Back to store"
              className="flex items-center gap-1.5 text-sm text-charcoal-500 transition-colors hover:text-sage-600"
            >
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Store</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10">
        <h1 className="font-serif text-3xl text-charcoal-900">Order Dashboard</h1>
        <p className="mt-1 text-sm text-charcoal-500">
          New orders land here instantly. Send the WhatsApp confirm-ask, ship, mark delivered — cancelled orders move to their own section.
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { id: "today-revenue", label: "Today Revenue", value: stats ? fmt(stats.today_revenue) : "—", icon: IndianRupee, accent: "text-sage-700" },
            { id: "total-revenue", label: "Total Revenue", value: stats ? fmt(stats.total_revenue) : "—", icon: IndianRupee, accent: "text-sage-700" },
            { id: "today-orders", label: "Today Orders", value: stats ? stats.today_orders : "—", icon: Package, accent: "text-charcoal-900" },
            { id: "total-orders", label: "Total Orders", value: stats ? stats.total_orders : "—", icon: Package, accent: "text-charcoal-900" },
            { id: "delivered", label: "Delivered", value: stats ? stats.delivered : "—", icon: CheckCircle2, accent: "text-sage-700" },
            { id: "cancelled", label: "Cancelled", value: stats ? stats.cancelled : "—", icon: XCircle, accent: "text-[#b91c1c]" },
            { id: "cod", label: "COD Orders", value: stats ? stats.cod_orders : "—", icon: Banknote, accent: "text-charcoal-900" },
            { id: "prepaid", label: "Prepaid Orders", value: stats ? stats.prepaid_orders : "—", icon: CreditCard, accent: "text-charcoal-900" },
          ].map((s) => (
            <div key={s.id} className="rounded-2xl border border-charcoal-900/10 bg-white p-4" data-testid={`stat-${s.id}`}>
              <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-charcoal-500">
                <s.icon className={`h-3.5 w-3.5 ${s.accent}`} /> {s.label}
              </p>
              <p className="mt-1 font-serif text-2xl text-charcoal-900">{s.value}</p>
            </div>
          ))}
        </div>

        {stats && stats.daywise && stats.daywise.length > 0 && (
          <div className="mt-4 overflow-hidden rounded-2xl border border-charcoal-900/10 bg-white" data-testid="admin-daywise">
            <p className="border-b border-charcoal-900/10 bg-oat-50 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-charcoal-500">
              Day-wise report — last 14 days (auto)
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs text-charcoal-700">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.14em] text-charcoal-500">
                    <th className="px-5 py-2.5 font-medium">Date</th>
                    <th className="px-3 py-2.5 font-medium">Orders</th>
                    <th className="px-3 py-2.5 font-medium">Revenue</th>
                    <th className="px-3 py-2.5 font-medium">COD</th>
                    <th className="px-3 py-2.5 font-medium">Prepaid</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.daywise.map((d) => {
                    const [y, m, dd] = d.date.split("-").map(Number);
                    const pretty = `${String(dd).padStart(2, "0")} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m - 1]} ${y}`;
                    const isToday = d.date === new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
                    return (
                      <tr key={d.date} className={`border-t border-charcoal-900/5 ${isToday ? "bg-sage-100/60" : ""}`}>
                        <td className="whitespace-nowrap px-5 py-2.5 font-medium">
                          {pretty}{isToday ? " · today" : ""}
                        </td>
                        <td className="px-3 py-2.5">{d.orders}</td>
                        <td className="px-3 py-2.5 font-semibold text-sage-700">{fmt(d.revenue)}</td>
                        <td className="px-3 py-2.5">{d.cod}</td>
                        <td className="px-3 py-2.5">{d.prepaid}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] transition-colors ${
                tab === t.id
                  ? "bg-charcoal-900 text-oat-100"
                  : "border border-charcoal-900/15 bg-white text-charcoal-500 hover:border-sage-500 hover:text-sage-600"
              }`}
            >
              {t.label}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  tab === t.id ? "bg-oat-100/20 text-oat-100" : "bg-oat-200 text-charcoal-700"
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
          {tab === "active" && unpaidCount > 0 && (
            <button
              onClick={removeFailedPayments}
              data-testid="admin-remove-unpaid"
              className="flex items-center gap-2 rounded-full border border-[#b91c1c]/30 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#b91c1c] transition-colors hover:bg-[#fdecec]"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove {unpaidCount} unpaid payment{unpaidCount === 1 ? "" : "s"}
            </button>
          )}
          {tab === "closed" && (
            <button
              onClick={removeAllCancelled}
              data-testid="admin-remove-all-cancelled"
              className="flex items-center gap-2 rounded-full border border-[#b91c1c]/30 bg-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#b91c1c] transition-colors hover:bg-[#fdecec]"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove all cancelled
            </button>
          )}
        </div>

        {loading ? (
          <p className="mt-10 text-center text-charcoal-500">Loading orders…</p>
        ) : list.length === 0 ? (
          <div className="mt-10 rounded-3xl border border-dashed border-charcoal-900/20 p-12 text-center" data-testid={`admin-empty-${tab}`}>
            <Package className="mx-auto h-10 w-10 text-sage-600" strokeWidth={1.4} />
            <p className="mt-4 font-serif text-2xl text-charcoal-900">
              {tab === "active" ? "No orders yet" : tab === "delivered" ? "No delivered orders yet" : "No cancelled or returned orders"}
            </p>
            <p className="mt-2 text-sm text-charcoal-500">
              {tab === "active"
                ? "Place a test order from the store and it will appear here instantly."
                : "Orders will move here when you mark them delivered, cancelled or returned."}
            </p>
          </div>
        ) : (
          <div className="mt-8 space-y-5">
            {list.map((o) => {
              const i = orders.indexOf(o);
              return (
              <article
                key={o.order_number}
                className={`rounded-3xl border border-charcoal-900/10 bg-white p-6 ${
                  o.status === "cancelled" || o.status === "returned" ? "opacity-70" : ""
                }`}
                data-testid={`admin-order-card-${i}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-charcoal-900/10 pb-4">
                  <div>
                    <p className="font-serif text-xl text-charcoal-900">{o.order_number}</p>
                    <p className="text-xs text-charcoal-500">{fmtDate(o.created_at)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                        o.payment_method === "cod"
                          ? "bg-gold/15 text-[#8a6a2f]"
                          : "bg-sage-100 text-sage-700"
                      }`}
                    >
                      {o.payment_method === "cod" ? (
                        <Banknote className="h-3.5 w-3.5" />
                      ) : (
                        <CreditCard className="h-3.5 w-3.5" />
                      )}
                      {o.payment_method === "cod" ? "Cash on Delivery" : "Pay Online"}
                    </span>
                    {o.payment_method === "razorpay" && o.payment_status !== "paid" && (
                      <span className="rounded-full bg-amber-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-700" data-testid={`admin-payment-pending-chip-${i}`}>
                        Payment not completed
                      </span>
                    )}
                    {o.payment_method === "cod" && (
                      <span
                        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${
                          o.call_status === "Customer confirmed on call"
                            ? "bg-sage-500 text-white"
                            : ["Cancelled on call", "Do Not Call", "Call Failed"].includes(o.call_status)
                              ? "bg-[#fdecec] text-[#b91c1c]"
                              : o.call_status
                                ? "bg-amber-100 text-amber-700"
                                : "bg-charcoal-900/10 text-charcoal-500"
                        }`}
                        data-testid={`admin-call-chip-${i}`}
                      >
                        <PhoneCall className="h-3.5 w-3.5" />
                        {o.call_status === "calling" ? "Calling…" : (o.call_status || "Not called")}{o.call_attempts ? ` · ${o.call_attempts}/3` : ""}
                      </span>
                    )}
                    {o.payment_method === "cod" && (
                      <button
                        onClick={() => callMonika(o.order_number, i)}
                        disabled={callState[i]?.loading}
                        data-testid={`admin-call-btn-${i}`}
                        className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-700 transition-colors hover:bg-sage-100 disabled:opacity-60"
                      >
                        <PhoneCall className="h-3.5 w-3.5" />
                        {callState[i]?.loading ? "Dialing…" : "Call via Monika"}
                      </button>
                    )}
                    {o.payment_status === "paid" && (
                      <span className="rounded-full bg-sage-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white" data-testid={`admin-paid-chip-${i}`}>
                        Paid
                      </span>
                    )}
                    {o.customer?.address_flag && (
                      <span
                        title={`Address needs a manual check (${o.customer.address_flag.replace(/_/g, " ")}) — WhatsApp-confirm before shipping`}
                        className="flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8a6a2f]"
                        data-testid={`admin-address-flag-chip-${i}`}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" /> Check address
                      </span>
                    )}
                    {o.customer_confirmation === "confirmed" && (
                      <span className="flex items-center gap-1 rounded-full bg-sage-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-700" data-testid={`admin-customer-confirm-chip-${i}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> Customer Confirmed
                      </span>
                    )}
                    {o.customer_confirmation === "cancelled" && (
                      <span className="flex items-center gap-1 rounded-full bg-[#fdecec] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#b91c1c]" data-testid={`admin-customer-cancel-chip-${i}`}>
                        <XCircle className="h-3.5 w-3.5" /> Customer Cancelled
                      </span>
                    )}
                    <span
                      className={`rounded-full px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] ${STATUS_CHIP[o.status] || STATUS_CHIP.confirmed}`}
                      data-testid={`admin-order-status-${i}`}
                    >
                      {STATUS_LABEL[o.status] || o.status}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 py-4 sm:grid-cols-2">
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-charcoal-500">Customer</p>
                    <p className="mt-1 font-medium text-charcoal-900">{o.customer.full_name}</p>
                    <p className="flex items-center gap-1.5 text-sm text-charcoal-500">
                      <Phone className="h-3.5 w-3.5 text-sage-600" /> {o.customer.phone || "—"}
                    </p>
                    <p className="text-sm text-charcoal-500">{o.customer.email}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-[0.14em] text-charcoal-500">
                      Ship to
                    </p>
                    <p className="mt-1 flex items-start gap-1.5 text-sm text-charcoal-700">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage-600" />
                      <span>
                        {o.customer.address}, {o.customer.city}, {o.customer.state} — {o.customer.zip}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-charcoal-900/10 pt-4">
                  <div className="min-w-0">
                    <p className="text-sm text-charcoal-700" data-testid={`admin-order-items-${i}`}>
                      {o.items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}
                    </p>
                    {o.shipment?.awb && (
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-charcoal-500" data-testid={`admin-shipment-info-${i}`}>
                        <Tag className="h-3.5 w-3.5 text-sage-600" />
                        AWB <span className="font-mono font-medium text-charcoal-900">{o.shipment.awb}</span>
                        · {o.shipment.courier || "Courier"}
                        {trackingText[i] && <> · {trackingText[i]}</>}
                      </p>
                    )}
                    {trackInfo[i]?.status && (
                      <p className="mt-1 text-xs text-sage-700" data-testid={`delhivery-track-status-${i}`}>
                        ✓ {trackInfo[i].status}{trackInfo[i].location ? ` · ${trackInfo[i].location}` : ""}{trackInfo[i].delivered_date ? ` · delivered ${String(trackInfo[i].delivered_date).slice(0, 10)}` : ""}
                      </p>
                    )}
                    {trackingText[i] && !o.shipment?.awb && (
                      <p className="mt-1 text-xs text-charcoal-500">{trackingText[i]}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-serif text-2xl text-charcoal-900">{fmt(o.total)}</p>

                    {o.status === "confirmed" && (
                      <>
                        {waLink(o) && (
                          <a
                            href={waLink(o)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-whatsapp-confirm-btn-${i}`}
                            className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-opacity hover:opacity-90"
                          >
                            WhatsApp Confirm
                          </a>
                        )}
                        <div className="w-full rounded-2xl border border-sage-500/25 bg-sage-100/40 p-3" data-testid={`delhivery-panel-${i}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <Truck className="h-4 w-4 text-sage-700" />
                            <span className="text-xs font-semibold uppercase tracking-[0.12em] text-sage-700">Delhivery</span>
                            <button
                              onClick={() => checkEstimate(o.order_number, i)}
                              disabled={delhivery[i]?.loading}
                              data-testid={`delhivery-estimate-btn-${i}`}
                              className="rounded-full border border-sage-500/40 bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-700 transition-colors hover:bg-sage-200 disabled:opacity-60"
                            >
                              {delhivery[i]?.loading ? "Checking…" : "Check shipping estimate"}
                            </button>
                          </div>
                          {delhivery[i]?.error && (
                            <p className="mt-2 text-xs text-[#b91c1c]" data-testid={`delhivery-estimate-error-${i}`}>{delhivery[i].error}</p>
                          )}
                          {delhivery[i]?.estimate && (
                            <p className="mt-2 text-xs text-sage-700" data-testid={`delhivery-estimate-${i}`}>
                              ✓ Serviceable · charge ₹{delhivery[i].estimate.total_amount || "0 (your Delhivery plan)"} · zone {delhivery[i].estimate.zone || "—"} · COD fee ₹{delhivery[i].estimate.cod_charge}
                            </p>
                          )}
                          {delhivery[i]?.estimate && (
                            <button
                              onClick={() => bookDelhivery(o.order_number, i)}
                              disabled={delhivery[i]?.loading}
                              data-testid={`delhivery-book-btn-${i}`}
                              className="mt-2 flex items-center gap-1.5 rounded-full bg-sage-600 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-sage-700 disabled:opacity-60"
                            >
                              <Truck className="h-3.5 w-3.5" /> Approve &amp; Book Shipment
                            </button>
                          )}
                        </div>
                        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto" data-testid={`awb-add-row-${i}`}>
                          <select
                            value={awbCourier[i] || "Bluedart"}
                            onChange={(e) => setAwbCourier((s) => ({ ...s, [i]: e.target.value }))}
                            data-testid={`awb-courier-select-${i}`}
                            className="shrink-0 rounded-full border border-charcoal-900/15 bg-white px-3 py-2.5 text-xs text-charcoal-900 outline-none focus:border-sage-500"
                          >
                            <option value="Bluedart">Bluedart</option>
                            <option value="Delhivery">Delhivery</option>
                          </select>
                          <input
                            value={awbInputs[i] || ""}
                            onChange={(e) => setAwbInputs((s) => ({ ...s, [i]: e.target.value }))}
                            placeholder={`Paste ${(awbCourier[i] || "Bluedart")} AWB / tracking ID`}
                            data-testid={`awb-input-${i}`}
                            className="min-w-0 flex-1 rounded-full border border-charcoal-900/15 bg-white px-4 py-2.5 text-xs text-charcoal-900 outline-none transition-colors focus:border-sage-500 sm:w-60 sm:flex-none"
                          />
                          <button
                            onClick={() => saveAwb(o.order_number, i)}
                            data-testid={`awb-save-btn-${i}`}
                            className="shrink-0 rounded-full bg-sage-600 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-sage-700"
                          >
                            <span className="flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Save &amp; Ship</span>
                          </button>
                        </div>
                        {srConfigured ? (
                          <button
                            onClick={() => shipViaRocket(o.order_number, i)}
                            data-testid={`admin-shiprocket-btn-${i}`}
                            className="flex items-center gap-2 rounded-full bg-charcoal-900 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
                          >
                            <Truck className="h-3.5 w-3.5" /> Ship via Shiprocket
                          </button>
                        ) : (
                          <button
                            onClick={() => setStatus(o.order_number, i, "shipped")}
                            data-testid={`admin-mark-shipped-btn-${i}`}
                            className="flex items-center gap-2 rounded-full bg-charcoal-900 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-oat-100 transition-colors hover:bg-sage-600"
                          >
                            <Truck className="h-3.5 w-3.5" /> Mark as Shipped
                          </button>
                        )}
                        <button
                          onClick={() => cancelOrder(o.order_number, i)}
                          data-testid={`admin-cancel-btn-${i}`}
                          className="flex items-center gap-1.5 rounded-full border border-[#b91c1c]/30 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-[#b91c1c] transition-colors hover:bg-[#fdecec]"
                        >
                          <XCircle className="h-3.5 w-3.5" /> Cancel
                        </button>
                      </>
                    )}

                    {o.status === "shipped" && (
                      <>
                        {o.shipment?.label_url && (
                          <>
                            <a
                              href={o.shipment.label_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`admin-label-link-${i}`}
                              className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-sage-100 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-sage-700 transition-colors hover:bg-sage-200"
                            >
                              <FileDown className="h-3.5 w-3.5" /> Label
                            </a>
                            <button
                              onClick={() => track(o.order_number, i)}
                              data-testid={`admin-track-btn-${i}`}
                              className="flex items-center gap-1.5 rounded-full border border-charcoal-900/15 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-charcoal-900 transition-colors hover:border-sage-500 hover:text-sage-700"
                            >
                              <Radar className="h-3.5 w-3.5" /> Track
                            </button>
                          </>
                        )}
                        {o.shipment?.courier === "Delhivery" && (
                          <>
                            <button
                              onClick={() => labelPdf(o.order_number, i, false)}
                              data-testid={`delhivery-label-btn-${i}`}
                              className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-sage-100 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-sage-700 transition-colors hover:bg-sage-200"
                            >
                              <FileDown className="h-3.5 w-3.5" /> Label
                            </button>
                            <button
                              onClick={() => labelPdf(o.order_number, i, true)}
                              data-testid={`delhivery-print-btn-${i}`}
                              className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-sage-100 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-sage-700 transition-colors hover:bg-sage-200"
                            >
                              <Printer className="h-3.5 w-3.5" /> Print
                            </button>
                            <button
                              onClick={() => refreshTrack(o.order_number, i)}
                              disabled={trackInfo[i]?.loading}
                              data-testid={`delhivery-refresh-btn-${i}`}
                              className="flex items-center gap-1.5 rounded-full border border-charcoal-900/15 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-charcoal-900 transition-colors hover:border-sage-500 hover:text-sage-700 disabled:opacity-60"
                            >
                              <Radar className={`h-3.5 w-3.5 ${trackInfo[i]?.loading ? "animate-spin" : ""}`} /> Refresh Tracking
                            </button>
                          </>
                        )}
                        {o.customer?.email && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await adminFetch(`/orders/${o.order_number}/email-tracking`, { method: "POST" });
                                const d = await res.json();
                                if (!res.ok) throw new Error(d.detail || "Could not send");
                                toast.success(`Tracking email sent to ${d.sent_to}`);
                              } catch (e) {
                                toast.error(e.message || "Could not send email");
                              }
                            }}
                            data-testid={`admin-email-tracking-btn-${i}`}
                            className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-sage-100 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-sage-700 transition-colors hover:bg-sage-200"
                          >
                            <Mail className="h-3.5 w-3.5" /> Email Tracking
                          </button>
                        )}
                        {waLink(o) && (
                          <a
                            href={waLink(o)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-whatsapp-btn-${i}`}
                            className="flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-opacity hover:opacity-90"
                          >
                            WhatsApp
                          </a>
                        )}
                        {smsLink(o) && (
                          <a
                            href={smsLink(o)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-sms-btn-${i}`}
                            className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-sage-100 px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-sage-700 transition-colors hover:bg-sage-200"
                          >
                            <MessageSquareText className="h-3.5 w-3.5" /> Send SMS
                          </a>
                        )}
                        <button
                          onClick={() => setStatus(o.order_number, i, "delivered")}
                          data-testid={`admin-delivered-btn-${i}`}
                          className="flex items-center gap-2 rounded-full bg-sage-600 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-white transition-colors hover:bg-sage-700"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" /> Delivered
                        </button>
                        <button
                          onClick={() => returnOrder(o.order_number, i)}
                          data-testid={`admin-return-btn-${i}`}
                          className="flex items-center gap-1.5 rounded-full border border-gold/50 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-[#8a6a2f] transition-colors hover:bg-gold/10"
                        >
                          <Undo2 className="h-3.5 w-3.5" /> Return
                        </button>
                      </>
                    )}

                    {(o.status === "delivered" || o.status === "return_requested") && (
                      <button
                        onClick={() => returnOrder(o.order_number, i)}
                        data-testid={`admin-return-btn-${i}`}
                        className="flex items-center gap-1.5 rounded-full border border-gold/50 bg-white px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-[#8a6a2f] transition-colors hover:bg-gold/10"
                      >
                        <Undo2 className="h-3.5 w-3.5" /> Return
                      </button>
                    )}

                    {o.status === "return_requested" && o.return_reason && (
                      <p className="mt-3 w-full rounded-xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800" data-testid={`admin-return-reason-${i}`}>
                        <b>Return reason:</b> {o.return_reason}
                      </p>
                    )}
                    {o.status === "return_requested" && (
                      <div className="w-full rounded-xl bg-oat-100 px-4 py-2.5 text-xs text-charcoal-700" data-testid={`admin-return-pickup-row-${i}`}>
                        {(o.return_pickup || {}).awb ? (
                          <span className="flex flex-wrap items-center gap-2">
                            <b>Return pickup:</b> Delhivery AWB <span className="font-mono font-semibold">{o.return_pickup.awb}</span>
                            <a href={o.return_pickup.label_url} target="_blank" rel="noreferrer" className="underline" data-testid={`admin-return-label-link-${i}`}>
                              Label
                            </a>
                          </span>
                        ) : (
                          <button
                            onClick={() => bookReturnPickup(o.order_number, i)}
                            disabled={returnPick[i]?.loading}
                            data-testid={`admin-return-pickup-btn-${i}`}
                            className="rounded-full border border-sage-500/40 bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-700 transition-colors hover:bg-sage-100 disabled:opacity-60"
                          >
                            {returnPick[i]?.loading ? "Booking…" : "Book return pickup (Delhivery)"}
                          </button>
                        )}
                      </div>
                    )}
                    {o.status === "returned" && !o.refund_upi && (
                      <div className="mt-3 flex w-full flex-wrap items-center gap-2 rounded-xl bg-sage-50 px-4 py-2.5" data-testid={`admin-refund-row-${i}`}>
                        <span className="text-xs font-medium text-charcoal-700">Refund ₹{o.total} to customer:</span>
                        <input
                          value={refundUpi[i] || ""}
                          onChange={(e) => setRefundUpi((s) => ({ ...s, [i]: e.target.value }))}
                          placeholder="customer UPI ID / bank ref"
                          data-testid={`admin-refund-upi-input-${i}`}
                          className="w-44 rounded-full border border-charcoal-900/15 bg-white px-3 py-1.5 text-xs text-charcoal-900 outline-none focus:border-sage-500"
                        />
                        <button
                          onClick={() => recordRefund(o.order_number, i, (refundUpi[i] || "").trim())}
                          disabled={(refundUpi[i] || "").trim().length < 5}
                          data-testid={`admin-refund-btn-${i}`}
                          className="rounded-full bg-sage-600 px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-sage-700 disabled:opacity-50"
                        >
                          Mark refunded
                        </button>
                      </div>
                    )}
                    {o.refunded_at && (
                      <span className="mt-3 rounded-full bg-sage-500 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-white" data-testid={`admin-refunded-chip-${i}`}>
                        Refunded ₹{o.refund_amount} → {o.refund_upi}
                      </span>
                    )}
                    {o.payment_method === "cod" && (o.call_last_error || (o.call_history || []).length > 0) && (
                      <div className="mt-3 rounded-xl bg-oat-50 px-4 py-2.5" data-testid={`admin-call-log-${i}`}>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-charcoal-500">Monika call log</p>
                        {o.call_last_error && (
                          <p className="mt-1 text-[11px] text-[#b91c1c]" data-testid={`admin-call-error-${i}`}>
                            Last error: {o.call_last_error}
                          </p>
                        )}
                        <ul className="mt-1 space-y-0.5">
                          {(o.call_history || []).slice(-3).reverse().map((h, k) => (
                            <li key={k} className="text-[11px] text-charcoal-500">
                              {fmtDate(h.at)} —{" "}
                              {h.event === "call_result"
                                ? `${h.result || "Result"}${h.status ? ` (${h.status})` : ""}`
                                : h.event === "call_error"
                                  ? `Call failed: ${String(h.detail || "").slice(0, 70)}`
                                  : "Call requested"}
                              {h.recording_url ? (
                                <span>
                                  {" · "}
                                  <a href={h.recording_url} target="_blank" rel="noreferrer" className="font-medium text-sage-700 underline" data-testid={`admin-call-recording-${i}-${k}`}>
                                    Listen
                                  </a>
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(o.status === "cancelled" || o.status === "returned") && (
                      <span
                        className={`flex items-center gap-1.5 rounded-full px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] ${
                          o.status === "cancelled" ? "bg-[#fdecec] text-[#b91c1c]" : "bg-[#efe7d8] text-[#8a6a2f]"
                        }`}
                        data-testid={`admin-closed-note-${i}`}
                      >
                        {o.status === "cancelled" ? <XCircle className="h-3.5 w-3.5" /> : <Undo2 className="h-3.5 w-3.5" />}
                        {STATUS_LABEL[o.status]}
                      </span>
                    )}
                    {o.status === "returned" && (
                      <button
                        onClick={() => undoReturn(o.order_number)}
                        data-testid={`admin-undo-return-btn-${i}`}
                        className="flex items-center gap-1.5 rounded-full border border-sage-500/40 bg-white px-3.5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-sage-700 transition-colors hover:bg-sage-100"
                      >
                        <RefreshCw className="h-3.5 w-3.5" /> Move back to Confirmed
                      </button>
                    )}
                  </div>
                </div>
              </article>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
